import React, { useEffect } from 'react';
import i18next from 'i18next';
import './MenuPageTranslate';
import useTableFilter from '../../components/TableFilter/useTableFilter';
import { loadMenuList, clearState, menuModule, loadOrganizations } from './MenuDucks';
import { useDispatch, useSelector } from 'react-redux';
import BreadCrumbs from '../../components/BreadCrumbs/BreadCrumbs';
import Paper from '@material-ui/core/Paper';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';
import FilterTreeSelect from '../../components/TableFilter/ColumnsFilters/FilterTreeSelect/FilterTreeSelect';
import Autocomplete from '../../components/TableFilter/ColumnsFilters/FilterAutocomplete';
import TextField from '@material-ui/core/TextField';
import FilterSelect from '../../components/TableFilter/ColumnsFilters/FilterSelect';
import useSimpleModal from '../../components/_hooks/useSimpleModal';
import Button from '../../_ui/Button/Button';
import SaveIcon from '@material-ui/icons/Save';
import AddEditMenuDialog from './MenuDialog/AddEditMenuDialog';
import { menuColumns } from './MenuColumns';
import useDict from '../../components/_hooks/useDict';
import { Table } from '../ServiceProvidersPage/ServiceProvidersStyles';
import Pagination from '@material-ui/lab/Pagination';
import {
  formatDate,
  privilegeRoles,
  providerRoles,
  restrictedRoles
} from './MenuPageUtils';

export default function MenuPage() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  // DICTIONARY
  const ageGroupsDict = useDict('gu_age_group') || [];
  const ageGroups = ageGroupsDict
    .filter(item => item.View === 'true')
    .map(item => ({ code: item.code, name: item.ru_name || item.kk_name }));

  //USE-SELECTOR
  const { organizations, tableData, loadingTable } = useSelector(
    state => state[menuModule]
  );
  const user = useSelector(state => state.login.user);
  const roles = user?.roles || [];

  // PERMISSIONS
  const hasRestrictedRole = roles.some(r => restrictedRoles.includes(r));
  const isProvider = roles.some(r => providerRoles.includes(r));
  const hasPrivilege = roles.some(r => privilegeRoles.includes(r));
  const hideProviderFilters = isProvider && !hasPrivilege;
  //Modal
  const addEditMenuDialog = useSimpleModal();

  // LOAD DATA
  const { filter, toPage, changeFilter } = useTableFilter(filter => {
    const menuFilter = { ...filter };
    delete menuFilter.serviceProviderLocation;
    const hasPrivilegeOrRestricted = roles.some(r =>
      [...privilegeRoles, ...restrictedRoles].includes(r)
    );
    if (!filter.startDate || !filter.ageGroupId) return;
    if (hasPrivilegeOrRestricted && !filter.serviceProviderId) return;
    dispatch(loadMenuList(menuFilter));
  });

  const serviceProviderLocation = filter.serviceProviderLocation;
  useEffect(() => {
    if (serviceProviderLocation) {
      dispatch(loadOrganizations(serviceProviderLocation));
    }
  }, [dispatch, serviceProviderLocation]);

  useEffect(() => {
    dispatch(clearState());
  }, [dispatch]);

  const breadCrumbItems = [
    { name: i18next.t('breadcrumbs_home'), href: '/' },
    { name: i18next.t('breadcrumbs_menuPage'), href: '/menu' }
  ];
  const formattedTableData = tableData.content.flatMap(row => {
    return row.menuItems.map(menuItem => ({
      ...menuItem,
      ageGroupId: row.ageGroupId,
      serviceProviderId: row.serviceProviderId,
      isActive: row.isActive
    }));
  });
  const handleDialogClose = () => {
    addEditMenuDialog.close();
    changeFilter({ startDate: '', ageGroupId: null });
  };

  return (
    <>
      <BreadCrumbs items={breadCrumbItems} />
      <Paper className="pb3 pt1 px3">
        <h3>{t('breadcrumbs_menuPage')}</h3>
        <Box
          className="flex items-center mt2"
          style={{ justifyContent: 'space-between', gap: 20 }}
        >
          <Box className="flex" style={{ gap: 20 }}>
            <Box className="flex justify-between items-center">
              <Typography style={{ fontSize: '14px', width: '200px' }}>
                {t('menuPage_startDate')}
              </Typography>
              <TextField
                type="date"
                value={filter.startDate || ''}
                onChange={e => {
                  const raw = e.target.value;
                  const formatted = formatDate(raw);
                  changeFilter({ startDate: formatted });
                }}
              />
            </Box>

            <Box className="flex justify-between items-center">
              <Typography style={{ fontSize: '14px', width: '150px' }}>
                {t('menuPage_ageGroup')}
              </Typography>
              <FilterSelect
                name="ageGroupId"
                options={ageGroups.map(item => ({
                  code: item.code,
                  ru_name: item.name,
                  kk_name: item.name
                }))}
                lang={i18n.language}
                fullWidth={false}
                style={{ marginRight: 10, width: 150 }}
                placeholder={t('contingentPage_group')}
              />
            </Box>

            {!hideProviderFilters && (
              <>
                <Box className="flex justify-between items-center">
                  <Typography style={{ fontSize: '12px', width: '200px' }}>
                    {t('colleagues_location')}
                  </Typography>
                  <FilterTreeSelect
                    lookupId="60"
                    name="serviceProviderLocation"
                    lang={i18n.language}
                    filter={item => item.ARM_gu === 'true'}
                  />
                </Box>

                <Box className="flex justify-between items-center">
                  <Typography style={{ fontSize: '12px', width: '200px' }}>
                    {t('colleagues_organizations')}
                  </Typography>
                  <Autocomplete name="serviceProviderId" options={organizations} />
                </Box>
              </>
            )}
          </Box>
          <Box className="flex" style={{ gap: 10 }}>
            {!hasRestrictedRole && (
              <>
                <Button
                  onClick={() => {
                    addEditMenuDialog.open({});
                  }}
                  icon={<SaveIcon fontSize="small" className="mr1" />}
                  text={t('menuPage_addButton')}
                />
                {tableData.content?.length > 0 && (
                  <Button
                    onClick={() => {
                      addEditMenuDialog.open(tableData.content);
                    }}
                    icon={<SaveIcon fontSize="small" className="mr1" />}
                    text={t('menuPage_editMenuTitle')}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
        <Box className="mt2">
          <Table
            columns={menuColumns({
              t
            })}
            data={formattedTableData}
            sortable={false}
            totalElements={tableData.totalElements}
            loading={loadingTable}
          />
          <Pagination
            onChange={(e, page) => toPage(page)}
            page={filter.pageNumber}
            count={Math.ceil(tableData.totalElements / filter.pageSize)}
            style={{ marginTop: 10 }}
            variant="outlined"
            shape="rounded"
          />
        </Box>

        {addEditMenuDialog.isOpen && (
          <AddEditMenuDialog
            {...addEditMenuDialog}
            open={addEditMenuDialog.isOpen}
            close={handleDialogClose}
            serviceProviderId={filter.serviceProviderId || user?.organization_id}
            initialData={addEditMenuDialog.data}
          />
        )}
      </Paper>
    </>
  );
}
